import type { AddressTagFilterTestIds } from 'application/components/AddressTagFilter/types';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import { AUDIO_REPORT_SCOPE, type AudioReportScopeType } from './types';

export const FIRST_REFETCH_INTERVAL = 1000;

export const MAX_ALLOW_DAYS: number = 90;
export const MAX_EXTERNAL_RECIPIENT_EMAILS_COUNT = 10;
export const MAX_REPORT_RECIPIENT_EMAILS_COUNT = 150;
export const MIN_DATE = DateUtils.subtractDaysFromStartToday(MAX_ALLOW_DAYS);
export const MAX_DATE = new Date();
export const DATE_RANGE_DESKTOP_MONTHS_SHOWN = 1;
export const DATE_RANGE_MOBILE_MONTHS_SHOWN = 12;
export const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_SEPARATOR_REGEXP = /[\s,;]+/;
export const BACKEND_ERROR_MESSAGES = {
  AD_REPORT_PERMISSION_DENIED: 'has not access to AD reports',
  TOO_MANY_RECIPIENTS: 'Field `emails_to` has too many values',
  TOO_MANY_EXTERNAL_RECIPIENTS: 'Field `emails_to` has too many external emails',
  INCORRECT_RECIPIENTS: 'Field `emails_to` has one or more incorrect emails',
  TOO_MANY_POINT_IDS: 'Field `point_ids` has too many values',
  INCORRECT_POINT_IDS: 'Invalid point IDs provided',
};

export const MAX_VISIBLE_ADDRESS_TAGS = 5;
export const ADDRESS_REPORT_POINTS_SKELETON_ROWS_COUNT = 4;
export const REPORT_RECIPIENTS_DROPDOWN_MAX_HEIGHT = 500;
export const REPORT_RECIPIENTS_DROPDOWN_MIN_HEIGHT = 160;
export const REPORT_RECIPIENTS_DROPDOWN_VIEWPORT_GAP = 16;
export const REPORT_RECIPIENTS_DROPDOWN_PREFERRED_HEIGHT = 280;

export const ADDRESSES_DROPDOWN_MAX_HEIGHT = 340;
export const ADDRESSES_DROPDOWN_MIN_HEIGHT = 160;
export const ADDRESSES_DROPDOWN_VIEWPORT_GAP = 16;
export const ADDRESSES_DROPDOWN_PREFERRED_HEIGHT = 280;
export const ADDRESSES_DROPDOWN_GAP = 4;

export const PROMOS_DROPDOWN_MAX_HEIGHT = 350;
export const PROMOS_DROPDOWN_MIN_HEIGHT = 160;
export const PROMOS_DROPDOWN_VIEWPORT_GAP = 16;
export const PROMOS_DROPDOWN_PREFERRED_HEIGHT = 350;
export const PROMOS_LIST_HEIGHT = 214;
export const PROMO_PAGE_SIZE = 50;

export const AUDIO_REPORT_SCOPE_OPTIONS: AudioReportScopeType[] = [
  AUDIO_REPORT_SCOPE.COMMON,
  AUDIO_REPORT_SCOPE.ADDRESSES,
  AUDIO_REPORT_SCOPE.PROMOS,
];

export const DATA_TEST_IDS = {
  REPORT_CANCEL_BUTTON: 'button_cancel_request_ad_campaign_report_modal',
  REPORT_SUBMIT_BUTTON: 'button_submit_request_ad_campaigns_report_modal',
  REPORT_DATE_RANGE_INPUT: 'input_ad_campaigns_report_date_range',
  DEFAULT_MODAL: 'modal_request_ad_campaigns_report_default',
  AUDIO_MODAL: 'modal_request_ad_campaigns_report_audio',
  AUDIO_MODAL_CONTENT: 'modal_request_ad_campaigns_report_audio_content',
  DEFAULT_DESCRIPTION: 'request_ad_campaigns_report_default_description',
  DEFAULT_DATE_RANGE: 'request_ad_campaigns_report_default_date_range',
  DEFAULT_FOOTER: 'request_ad_campaigns_report_default_footer',
  AUDIO_DESCRIPTION: 'request_ad_campaigns_report_audio_description',
  AUDIO_DATE_RANGE_FIELD: 'request_ad_campaigns_report_audio_date_range_field',
  AUDIO_DATE_RANGE_LABEL: 'request_ad_campaigns_report_audio_date_range_label',
  AUDIO_REPORT_SCOPE: 'request_ad_campaigns_report_audio_scope',
  AUDIO_REPORT_SCOPE_LABEL: 'request_ad_campaigns_report_audio_scope_label',
  AUDIO_REPORT_SCOPE_SELECT: 'select_request_ad_campaigns_report_audio_scope',
  AUDIO_REPORT_SCOPE_ICON: 'request_ad_campaigns_report_audio_scope_icon',
  AUDIO_REPORT_SCOPE_OPTION: (scope: string) => `request_ad_campaigns_report_audio_scope_option_${scope}`,
  AUDIO_REPORT_PROMOS: 'request_ad_campaigns_report_audio_promos',
  AUDIO_REPORT_PROMOS_SHELL: 'request_ad_campaigns_report_audio_promos_shell',
  AUDIO_REPORT_PROMOS_LABEL: 'request_ad_campaigns_report_audio_promos_label',
  AUDIO_REPORT_PROMOS_TRIGGER: 'button_request_ad_campaigns_report_audio_promos',
  AUDIO_REPORT_PROMOS_TRIGGER_ICON: 'icon_request_ad_campaigns_report_audio_promos_trigger',
  AUDIO_REPORT_PROMOS_TRIGGER_TITLE: 'request_ad_campaigns_report_audio_promos_trigger_title',
  AUDIO_REPORT_PROMOS_TRIGGER_COUNT: 'request_ad_campaigns_report_audio_promos_trigger_count',
  AUDIO_REPORT_PROMOS_DROPDOWN: 'dropdown_request_ad_campaigns_report_audio_promos',
  AUDIO_REPORT_PROMOS_TOOLBAR: 'request_ad_campaigns_report_audio_promos_toolbar',
  AUDIO_REPORT_PROMOS_SEARCH_CONTAINER: 'request_ad_campaigns_report_audio_promos_search_container',
  AUDIO_REPORT_PROMOS_SEARCH: 'input_request_ad_campaigns_report_audio_promos_search',
  AUDIO_REPORT_PROMOS_SORT: 'button_request_ad_campaigns_report_audio_promos_sort',
  AUDIO_REPORT_PROMOS_SORT_ICON: 'icon_request_ad_campaigns_report_audio_promos_sort',
  AUDIO_REPORT_PROMOS_SORT_TOOLTIP: 'tooltip_request_ad_campaigns_report_audio_promos_sort',
  AUDIO_REPORT_PROMOS_FILTER: 'button_request_ad_campaigns_report_audio_promos_filter',
  AUDIO_REPORT_PROMOS_FILTER_BADGE: 'request_ad_campaigns_report_audio_promos_filter_badge',
  AUDIO_REPORT_PROMOS_FILTER_ICON: 'icon_request_ad_campaigns_report_audio_promos_filter',
  AUDIO_REPORT_PROMOS_STATUS_FILTER: 'request_ad_campaigns_report_audio_promos_status_filter',
  AUDIO_REPORT_PROMOS_STATUS_FILTER_OPTION: (status: string) =>
    `request_ad_campaigns_report_audio_promos_status_filter_option_${status}`,
  AUDIO_REPORT_PROMOS_STATUS_FILTER_OPTION_CHECKBOX: (status: string) =>
    `checkbox_request_ad_campaigns_report_audio_promos_status_filter_${status}`,
  AUDIO_REPORT_PROMOS_STATUS_FILTER_OPTION_INDICATOR: (status: string) =>
    `request_ad_campaigns_report_audio_promos_status_filter_indicator_${status}`,
  AUDIO_REPORT_PROMOS_DIVIDER: 'request_ad_campaigns_report_audio_promos_divider',
  AUDIO_REPORT_PROMOS_SELECTION_HEADER: 'request_ad_campaigns_report_audio_promos_selection_header',
  AUDIO_REPORT_PROMOS_SELECTED_COUNT: 'request_ad_campaigns_report_audio_promos_selected_count',
  AUDIO_REPORT_PROMOS_SELECT_ALL: 'button_request_ad_campaigns_report_audio_promos_select_all',
  AUDIO_REPORT_PROMOS_RESET: 'button_request_ad_campaigns_report_audio_promos_reset',
  AUDIO_REPORT_PROMOS_LIST: 'request_ad_campaigns_report_audio_promos_list',
  AUDIO_REPORT_PROMOS_VIRTUAL_LIST: 'request_ad_campaigns_report_audio_promos_virtual_list',
  AUDIO_REPORT_PROMOS_SKELETON: 'request_ad_campaigns_report_audio_promos_skeleton',
  AUDIO_REPORT_PROMOS_NEXT_PAGE_SKELETON: 'request_ad_campaigns_report_audio_promos_next_page_skeleton',
  AUDIO_REPORT_PROMOS_EMPTY: 'request_ad_campaigns_report_audio_promos_empty',
  AUDIO_REPORT_PROMOS_ERROR: 'request_ad_campaigns_report_audio_promos_error',
  AUDIO_REPORT_PROMOS_RETRY: 'button_request_ad_campaigns_report_audio_promos_retry',
  AUDIO_REPORT_PROMOS_SKELETON_ROW: (index: number) => `request_ad_campaigns_report_audio_promos_skeleton_row_${index}`,
  AUDIO_REPORT_PROMOS_OPTION: (promoId: string) => `request_ad_campaigns_report_audio_promo_option_${promoId}`,
  AUDIO_REPORT_PROMOS_OPTION_TITLE: (promoId: string) =>
    `request_ad_campaigns_report_audio_promo_option_${promoId}_title`,
  AUDIO_REPORT_PROMOS_OPTION_CAMPAIGN: (promoId: string) =>
    `request_ad_campaigns_report_audio_promo_option_${promoId}_campaign`,
  AUDIO_REPORT_PROMOS_OPTION_STATUS: (promoId: string) =>
    `request_ad_campaigns_report_audio_promo_option_${promoId}_status`,
  AUDIO_REPORT_PROMOS_OPTION_CHECKBOX: (promoId: string) =>
    `checkbox_request_ad_campaigns_report_audio_promo_${promoId}`,
  AUDIO_REPORT_PROMOS_OPTION_PLAY: (promoId: string) =>
    `button_request_ad_campaigns_report_audio_promo_play_${promoId}`,
  AUDIO_REPORT_PROMOS_OPTION_PROGRESS: (promoId: string) =>
    `request_ad_campaigns_report_audio_promo_progress_${promoId}`,
  AUDIO_REPORT_PROMOS_OPTION_PROGRESS_INPUT: (promoId: string) => `promo_report_progress_${promoId}`,
  AUDIO_REPORT_ADDRESSES: 'request_ad_campaigns_report_audio_addresses',
  AUDIO_REPORT_ADDRESSES_LABEL: 'request_ad_campaigns_report_audio_addresses_label',
  AUDIO_REPORT_ADDRESSES_TRIGGER: 'button_request_ad_campaigns_report_audio_addresses',
  AUDIO_REPORT_ADDRESSES_TRIGGER_TEXT: 'request_ad_campaigns_report_audio_addresses_trigger_text',
  AUDIO_REPORT_ADDRESSES_TRIGGER_TITLE: 'request_ad_campaigns_report_audio_addresses_trigger_title',
  AUDIO_REPORT_ADDRESSES_TRIGGER_COUNT: 'request_ad_campaigns_report_audio_addresses_trigger_count',
  AUDIO_REPORT_ADDRESSES_TRIGGER_ICON: 'request_ad_campaigns_report_audio_addresses_trigger_icon',
  AUDIO_REPORT_ADDRESSES_DROPDOWN: 'dropdown_request_ad_campaigns_report_audio_addresses',
  AUDIO_REPORT_ADDRESSES_SEARCH: 'input_request_ad_campaigns_report_audio_addresses_search',
  AUDIO_REPORT_ADDRESSES_FILTER_BUTTON: 'button_request_ad_campaigns_report_audio_addresses_filter',
  AUDIO_REPORT_ADDRESSES_FILTER_BADGE: 'request_ad_campaigns_report_audio_addresses_filter_badge',
  AUDIO_REPORT_ADDRESSES_TAG_FILTER: 'request_ad_campaigns_report_audio_addresses_tag_filter',
  AUDIO_REPORT_ADDRESSES_TAG_FILTER_SEARCH: 'input_request_ad_campaigns_report_audio_addresses_tag_filter_search',
  AUDIO_REPORT_ADDRESSES_EXACT_TOGGLE: 'switcher_request_ad_campaigns_report_audio_addresses_exact',
  AUDIO_REPORT_ADDRESSES_EXACT_INFO: 'request_ad_campaigns_report_audio_addresses_exact_info',
  AUDIO_REPORT_ADDRESSES_EXACT_TOOLTIP: 'tooltip_request_ad_campaigns_report_audio_addresses_exact',
  AUDIO_REPORT_ADDRESSES_APPLIED_TAGS: 'request_ad_campaigns_report_audio_addresses_applied_tags',
  AUDIO_REPORT_ADDRESSES_APPLIED_TAG: (tagId: string) =>
    `request_ad_campaigns_report_audio_addresses_applied_tag_${tagId}`,
  AUDIO_REPORT_ADDRESSES_APPLIED_TAG_REMOVE_BUTTON: (tagId: string) =>
    `button_request_ad_campaigns_report_audio_addresses_applied_tag_remove_${tagId}`,
  AUDIO_REPORT_ADDRESSES_APPLIED_TAGS_MORE_BUTTON:
    'button_request_ad_campaigns_report_audio_addresses_applied_tags_more',
  AUDIO_REPORT_ADDRESSES_CLEAR_TAGS_BUTTON: 'button_request_ad_campaigns_report_audio_addresses_clear_tags',
  AUDIO_REPORT_ADDRESSES_DIVIDER: 'request_ad_campaigns_report_audio_addresses_divider',
  AUDIO_REPORT_ADDRESSES_HEADER: 'request_ad_campaigns_report_audio_addresses_header',
  AUDIO_REPORT_ADDRESSES_SELECTED_COUNT: 'request_ad_campaigns_report_audio_addresses_selected_count',
  AUDIO_REPORT_ADDRESSES_ACTIONS: 'request_ad_campaigns_report_audio_addresses_actions',
  AUDIO_REPORT_ADDRESSES_SELECT_ALL_BUTTON: 'button_request_ad_campaigns_report_audio_addresses_select_all',
  AUDIO_REPORT_ADDRESSES_RESET_BUTTON: 'button_request_ad_campaigns_report_audio_addresses_reset',
  AUDIO_REPORT_ADDRESSES_LIST: 'request_ad_campaigns_report_audio_addresses_list',
  AUDIO_REPORT_ADDRESSES_SKELETON: 'request_ad_campaigns_report_audio_addresses_skeleton',
  AUDIO_REPORT_ADDRESSES_SKELETON_ROW: (index: number) =>
    `request_ad_campaigns_report_audio_addresses_skeleton_row_${index}`,
  AUDIO_REPORT_ADDRESSES_EMPTY: 'request_ad_campaigns_report_audio_addresses_empty',
  AUDIO_REPORT_ADDRESS_OPTION: (addressId: string) => `request_ad_campaigns_report_audio_address_option_${addressId}`,
  AUDIO_REPORT_ADDRESS_OPTION_CHECKBOX: (addressId: string) =>
    `checkbox_request_ad_campaigns_report_audio_address_${addressId}`,
  AUDIO_REPORT_ADDRESS_OPTION_TEXT: (addressId: string) =>
    `request_ad_campaigns_report_audio_address_option_text_${addressId}`,
  AUDIO_REPORT_ADDRESS_OPTION_COMMENT: (addressId: string) =>
    `request_ad_campaigns_report_audio_address_option_comment_${addressId}`,
  AUDIO_REPORT_ADDRESS_OPTION_COMMENT_TEXT: (addressId: string) =>
    `request_ad_campaigns_report_audio_address_option_comment_text_${addressId}`,
  AUDIO_REPORT_ADDRESS_OPTION_TAG: (addressId: string, tagId: string) =>
    `request_ad_campaigns_report_audio_address_option_${addressId}_tag_${tagId}`,
  AUDIO_REPORT_ADDRESS_OPTION_MORE_TAGS_BUTTON: (addressId: string) =>
    `button_request_ad_campaigns_report_audio_address_option_${addressId}_more_tags`,
  AUDIO_REPORT_ADDRESS_OPTION_HIDE_TAGS_BUTTON: (addressId: string) =>
    `button_request_ad_campaigns_report_audio_address_option_${addressId}_hide_tags`,
  AUDIO_REPORT_ADDRESS_TAG_OPTION: (tagId: string) => `request_ad_campaigns_report_audio_addresses_tag_option_${tagId}`,
  AUDIO_REPORT_ADDRESS_TAG_OPTION_CHECKBOX: (tagId: string) =>
    `checkbox_request_ad_campaigns_report_audio_addresses_tag_${tagId}`,
  AUDIO_RECIPIENTS: 'request_ad_campaigns_report_audio_recipients',
  ADD_EXTERNAL_RECIPIENTS_BUTTON: 'button_request_ad_campaigns_report_add_external_recipients',
  ADD_EXTERNAL_RECIPIENTS_TEXT: 'request_ad_campaigns_report_add_external_recipients_text',
  RECIPIENTS_SELECT: 'request_ad_campaigns_report_recipients_select',
  RECIPIENTS_SELECT_LABEL: 'request_ad_campaigns_report_recipients_select_label',
  RECIPIENTS_SELECT_TRIGGER: 'button_request_ad_campaigns_report_recipients_select',
  RECIPIENTS_SELECT_TRIGGER_TEXT: 'request_ad_campaigns_report_recipients_select_trigger_text',
  RECIPIENTS_SELECT_TRIGGER_TITLE: 'request_ad_campaigns_report_recipients_select_trigger_title',
  RECIPIENTS_SELECT_TRIGGER_COUNT: 'request_ad_campaigns_report_recipients_select_trigger_count',
  RECIPIENTS_SELECT_TRIGGER_ICON: 'request_ad_campaigns_report_recipients_select_trigger_icon',
  RECIPIENTS_SELECT_DROPDOWN: 'dropdown_request_ad_campaigns_report_recipients_select',
  RECIPIENTS_SELECT_SEARCH: 'input_request_ad_campaigns_report_recipients_search',
  RECIPIENTS_SELECT_DIVIDER: 'request_ad_campaigns_report_recipients_select_divider',
  RECIPIENTS_SELECT_HEADER: 'request_ad_campaigns_report_recipients_select_header',
  RECIPIENTS_SELECT_SELECTED_COUNT: 'request_ad_campaigns_report_recipients_select_selected_count',
  RECIPIENTS_SELECT_ACTIONS: 'request_ad_campaigns_report_recipients_select_actions',
  RECIPIENTS_SELECT_ACTIONS_DIVIDER: 'request_ad_campaigns_report_recipients_select_actions_divider',
  RECIPIENTS_SELECT_SELECT_ALL_BUTTON: 'button_request_ad_campaigns_report_recipients_select_all',
  RECIPIENTS_SELECT_RESET_BUTTON: 'button_request_ad_campaigns_report_recipients_reset',
  RECIPIENTS_SELECT_LIST: 'request_ad_campaigns_report_recipients_select_list',
  RECIPIENTS_SELECT_EMPTY: 'request_ad_campaigns_report_recipients_select_empty',
  RECIPIENT_OPTION: (recipientId: string) => `request_ad_campaigns_report_recipient_option_${recipientId}`,
  RECIPIENT_OPTION_CHECKBOX: (recipientId: string) => `checkbox_request_ad_campaigns_report_recipient_${recipientId}`,
  RECIPIENT_OPTION_EMAIL: (recipientId: string) => `request_ad_campaigns_report_recipient_option_email_${recipientId}`,
  EXTERNAL_RECIPIENTS: 'request_ad_campaigns_report_external_recipients',
  EXTERNAL_RECIPIENTS_LABEL: 'request_ad_campaigns_report_external_recipients_label',
  EXTERNAL_RECIPIENTS_INPUT_CONTAINER: 'request_ad_campaigns_report_external_recipients_input_container',
  EXTERNAL_RECIPIENTS_INPUT_WRAPPER: 'request_ad_campaigns_report_external_recipients_input_wrapper',
  EXTERNAL_RECIPIENTS_INPUT: 'input_request_ad_campaigns_report_external_recipients',
  EXTERNAL_RECIPIENT_CHIP: (index: number) => `request_ad_campaigns_report_external_recipient_chip_${index}`,
  EXTERNAL_RECIPIENT_CHIP_REMOVE_BUTTON: (index: number) =>
    `button_request_ad_campaigns_report_external_recipient_chip_remove_${index}`,
} as const;

export const ADDRESS_REPORT_TAG_FILTER_TEST_IDS: AddressTagFilterTestIds = {
  mainSearch: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_SEARCH,
  filterButton: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_FILTER_BUTTON,
  filterBadge: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_FILTER_BADGE,
  dropdown: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_TAG_FILTER,
  tagSearch: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_TAG_FILTER_SEARCH,
  exactSwitcher: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_EXACT_TOGGLE,
  exactInfo: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_EXACT_INFO,
  exactTooltip: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_EXACT_TOOLTIP,
  tagOption: DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_TAG_OPTION,
  tagOptionCheckbox: DATA_TEST_IDS.AUDIO_REPORT_ADDRESS_TAG_OPTION_CHECKBOX,
  appliedTags: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_APPLIED_TAGS,
  appliedTag: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_APPLIED_TAG,
  appliedTagRemove: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_APPLIED_TAG_REMOVE_BUTTON,
  appliedTagsMore: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_APPLIED_TAGS_MORE_BUTTON,
  appliedTagsClear: DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_CLEAR_TAGS_BUTTON,
};
